import { inject, observer } from 'mobx-react';

import React, { useState } from 'react';
import withTranslate, { Translate, WithTranslateProps } from '../common/WithTranslate';
import { Instance } from 'mobx-state-tree';
import { ApplicationStore, AuthenticationStore } from '../../store';
import { Form } from 'react-final-form';
import styled from '@emotion/styled';
import { RouteComponentProps } from 'react-router-dom';
import { PasswordInputFormField } from '../common/form';
import { Page, TLCode } from '../../common/enums';
import { Button, Intent, Spinner } from '@blueprintjs/core';
import { PasswordResetConfirmation } from './PasswordResetConfirmation';

interface NewPasswordPageProps extends WithTranslateProps, RouteComponentProps<{ Id: string }> {
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
}

interface NewPasswordPageState {
  locationPathId: string | null;
  isIdValid: boolean | undefined;
}

interface NewPassordProps {
  translate: Translate;
  id: string | null;
  sendNewPassword: (id: string, password: string, passwordConfirmation: string) => boolean;
  isIdValid: boolean | undefined;
  onNewPasswordRequested: () => void;
  onNewPasswordRequestInvalid: () => void;
}

const NewPasswordContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DivCenter = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-bottom: 5px;
  justify-content: center;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const NewPassword = ({
  translate,
  id,
  sendNewPassword,
  isIdValid,
  onNewPasswordRequested,
  onNewPasswordRequestInvalid,
}: NewPassordProps) => {
  const [inProgress, setInProgressState] = useState(false);
  const [done, setDoneState] = useState(false);
  const [inError, setInErrorState] = useState(false);
  if (isIdValid === undefined) return <Spinner />;
  if (!isIdValid)
    return (
      <NewPasswordContainer>
        <Container>
          <span>{translate(TLCode.LOGIN_NewPasswordError)}</span>
          <br />
          <DivLine>
            <DivCenter>
              <Button
                text={translate(TLCode.LOGIN_NewPasswordReturnPage)}
                intent={Intent.PRIMARY}
                onClick={onNewPasswordRequestInvalid}
              />
            </DivCenter>
          </DivLine>
        </Container>
      </NewPasswordContainer>
    );
  return (
    <NewPasswordContainer>
      <PasswordResetConfirmation
        inProgress={inProgress}
        inError={inError}
        done={done}
        translate={translate}
        onClose={onNewPasswordRequested}
      />
      <Form
        onSubmit={async (values: any) => {
          setInProgressState(true);
          const result = await sendNewPassword(id !== null ? id : '', values.password, values.passwordConfirmation);
          setInProgressState(false);
          if (result === true) setDoneState(true);
          else setInErrorState(true);
        }}
        render={({ handleSubmit, hasValidationErrors, dirty, values }) => (
          <form onSubmit={handleSubmit}>
            <Container>
              <h1>
                Pharmagarde
                <br />
              </h1>
              <br />
              <PasswordInputFormField
                name="password"
                required={true}
                inline={true}
                fill={true}
                validate={[]}
                placeholder={TLCode.NewPassword}
              />
              <PasswordInputFormField
                name="passwordConfirmation"
                required={true}
                inline={true}
                fill={true}
                validate={[]}
                placeholder={TLCode.NewPasswordConfirmation}
              />
              <br />
              <DivLine>
                <DivCenter>
                  {
                    <Button
                      type={'submit'}
                      text={translate(TLCode.GENERAL_Save)}
                      intent={Intent.PRIMARY}
                      disabled={hasValidationErrors || !dirty || values.password !== values.passwordConfirmation}
                    />
                  }
                </DivCenter>
              </DivLine>
            </Container>
          </form>
        )}
      />
    </NewPasswordContainer>
  );
};

@inject('AuthenticationStore')
@inject('ApplicationStore')
@observer
class NewPasswordPage extends React.Component<NewPasswordPageProps, NewPasswordPageState> {
  state = {
    locationPathId: null,
    isIdValid: undefined,
  } as NewPasswordPageState;

  async componentDidMount() {
    await this.setNewPasswordRequestId();
  }

  private setNewPasswordRequestId = async () => {
    const { location } = this.props;
    const query = new URLSearchParams(location.search);

    const id = query.get('Id');

    if (id !== null) {
      const isIdValid = ((await this.props.AuthenticationStore!.isRequestNewPasswordValid(id)) as unknown) as boolean;
      this.setState({ locationPathId: id, isIdValid: isIdValid });
    } else {
      this.setState({ isIdValid: false });
    }
  };

  private sendNewPassword = (id: string, password: string, passwordConfirmation: string) => {
    const result = (this.props.AuthenticationStore!.newPassword(
      id,
      password,
      passwordConfirmation,
    ) as unknown) as boolean;
    return result;
  };

  render() {
    return (
      <NewPassword
        translate={this.props.translate}
        id={this.state.locationPathId}
        sendNewPassword={this.sendNewPassword}
        isIdValid={this.state.isIdValid}
        onNewPasswordRequested={() => {
          this.props.ApplicationStore!.navigate(Page.Login);
        }}
        onNewPasswordRequestInvalid={() => {
          this.props.ApplicationStore!.navigate(Page.Login);
        }}
      />
    );
  }
}

export default withTranslate(NewPasswordPage);
